import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import MarketingArt from "../../components/marketing/MarketingArt.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";
import { breadcrumbSchema, faqSchema } from "../../seo/schema.js";

const FAQS = [
  {
    q: "Who has to file Form 2290?",
    a: "Anyone who registers — or is required to register — a highway motor vehicle with a taxable gross weight of 55,000 pounds or more in their name, once it's first used on a public highway during the tax period. That includes individuals, LLCs, corporations, partnerships, and nonprofits.",
  },
  {
    q: "What counts as a taxable vehicle?",
    a: "A self-propelled vehicle designed to carry a load over public highways — trucks, truck tractors, and buses are typical examples. Vans, pickups, and panel trucks usually fall under the 55,000 lb threshold and aren't taxable.",
  },
  {
    q: "When is Form 2290 due?",
    a: "By the last day of the month following the month the vehicle was first used during the period (July 1, 2026 – June 30, 2027). For example, a truck first used in July 2026 must be reported by August 31, 2026. See the full month-by-month chart on the How it works page.",
  },
  {
    q: "Is electronic filing required?",
    a: "Yes, if you're reporting and paying tax on 25 or more vehicles in a single return during the period. Suspended (category W) vehicles don't count toward that 25, since no tax is being paid on them. Everyone else is encouraged, but not required, to e-file.",
  },
  {
    q: "What's the mileage use limit, and what happens if I go over it?",
    a: "A vehicle expected to be used 5,000 miles or less (7,500 miles or less for agricultural vehicles) during the period can be reported as suspended — category W — with no tax due. If it later exceeds that limit, the tax becomes due and must be reported on Form 2290 for that period.",
  },
  {
    q: "Do logging vehicles pay a different rate?",
    a: "Yes. A vehicle used exclusively to transport products harvested from a forested site, and registered as a logging vehicle under state law, is taxed at a reduced rate in every weight category — see the Rates page for the exact numbers.",
  },
  {
    q: "What do I get after I file?",
    a: "A stamped Schedule 1 — your proof of payment for state vehicle registration, and for Customs and Border Protection if you're bringing a Canadian or Mexican-registered vehicle into the U.S. Filed electronically, it's typically available within seconds of IRS acceptance.",
  },
  {
    q: "What if I sold, destroyed, or lost a vehicle mid-period?",
    a: "You may be able to claim a credit for tax already paid on a vehicle that was destroyed, stolen, or sold before June 1 and not used for the rest of the period, or that was used 5,000 miles or less (7,500 for agricultural) during the period.",
  },
  {
    q: "What happens if I file or pay late?",
    a: "The IRS can charge a penalty plus interest on top of the tax owed for filing or paying late. If you believe you have reasonable cause, you can request penalty relief by writing to the IRS or visiting IRS.gov/PenaltyRelief — don't attach an explanation to the return itself.",
  },
  {
    q: "Do I need an EIN to file?",
    a: "Yes — Form 2290 requires an employer identification number (EIN); a Social Security number can't be used. If you don't have one yet, you can apply online at IRS.gov/EIN.",
  },
];

const meta = ROUTE_META["/faq"];

const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <div className="max-w-[860px] mx-auto px-[22px] py-[46px] pb-[70px]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/faq"
        keywords={meta.keywords}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
          // The FAQS array renders on this page (the accordion below), so the
          // markup describes visible content.
          faqSchema(FAQS),
        ]}
      />
      <PageHeader eyebrow="Frequently asked questions" title="Straight from the Form 2290 instructions">
        Short answers, sourced from the IRS instructions for Form 2290 (Rev. July 2026). For anything more
        specific to your situation, the full instructions are at IRS.gov/Form2290.
      </PageHeader>

      <MarketingArt src="faq.webp" className="w-full max-w-[520px] h-auto mt-7" />

      <Panel className="mt-8 divide-y divide-[var(--line)] overflow-hidden">
        {FAQS.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={item.q}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 text-left p-[16px_18px] bg-transparent border-0 cursor-pointer"
              >
                <span className="font-semibold text-[15px]">{item.q}</span>
                <ChevronDown
                  size={18}
                  className={["flex-none text-[var(--muted)] transition-transform", isOpen ? "rotate-180" : ""].join(" ")}
                />
              </button>
              {isOpen && (
                <div className="px-[18px] pb-[18px] text-[var(--muted)] text-sm max-w-[68ch]">{item.a}</div>
              )}
            </div>
          );
        })}
      </Panel>

      <Panel padded className="!bg-[var(--accent-soft)] flex flex-wrap items-center justify-between gap-6 mt-10 !p-[30px]">
        <div>
          <h2 className="text-[20px] font-disp">Still have a question?</h2>
          <p className="text-[var(--muted)] mt-1">Create an account and EasyFile2290 walks you through the rest.</p>
        </div>
        <Button as={Link} to="/login?tab=up" size="lg" className="no-underline flex-none">
          Get started free <ArrowRight size={17} />
        </Button>
      </Panel>
    </div>
  );
};

export default FAQ;
